import type { ModelSalesFunnelStageLeads } from '@app/api/SalesFunnels';

const getStageLeads = (stage: ModelSalesFunnelStageLeads) => stage.Leads ?? [];

const clampIndex = (index: number, length: number): number => {
  if (index < 0) return 0;
  if (index > length) return length;

  return index;
};

export const findStageIdByLeadId = (
  stages: ModelSalesFunnelStageLeads[],
  leadId: number
): number | null => {
  const stage = stages.find((item) =>
    getStageLeads(item).some((lead) => lead.Id === leadId)
  );

  return stage ? stage.StageId : null;
};

export const relocateStageLeadForPreview = (
  stages: ModelSalesFunnelStageLeads[],
  leadId: number,
  toStageId: number,
  toIndex: number
): ModelSalesFunnelStageLeads[] => {
  const sourceStage = stages.find((stage) =>
    getStageLeads(stage).some((lead) => lead.Id === leadId)
  );

  if (!sourceStage) return stages;

  const movedLead = getStageLeads(sourceStage).find(
    (lead) => lead.Id === leadId
  );

  if (!movedLead) return stages;

  return stages.map((stage) => {
    const leads = getStageLeads(stage).filter((lead) => lead.Id !== leadId);

    if (stage.StageId !== toStageId) {
      if (stage.StageId === sourceStage.StageId) {
        return { ...stage, Leads: leads };
      }

      return stage;
    }

    const insertIndex = clampIndex(toIndex, leads.length);
    const nextLeads = [
      ...leads.slice(0, insertIndex),
      movedLead,
      ...leads.slice(insertIndex),
    ];

    return { ...stage, Leads: nextLeads };
  });
};

export const reorderStageLeadForPreview = (
  stages: ModelSalesFunnelStageLeads[],
  stageId: number,
  fromIndex: number,
  toIndex: number
): ModelSalesFunnelStageLeads[] => {
  if (fromIndex === toIndex) return stages;

  return stages.map((stage) => {
    if (stage.StageId !== stageId) return stage;

    const leads = [...getStageLeads(stage)];

    if (fromIndex < 0 || fromIndex >= leads.length) return stage;

    const [movedLead] = leads.splice(fromIndex, 1);
    leads.splice(clampIndex(toIndex, leads.length), 0, movedLead);

    return { ...stage, Leads: leads };
  });
};

export const applyStageLeadMoveTotals = (
  stages: ModelSalesFunnelStageLeads[],
  fromStageId: number,
  toStageId: number
): ModelSalesFunnelStageLeads[] => {
  if (fromStageId === toStageId) return stages;

  return stages.map((stage) => {
    if (stage.StageId === fromStageId) {
      return { ...stage, TotalLeads: Math.max(0, stage.TotalLeads - 1) };
    }

    if (stage.StageId === toStageId) {
      return { ...stage, TotalLeads: stage.TotalLeads + 1 };
    }

    return stage;
  });
};
